import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import './FactionPage.css';
import PageButton from '../../elements/PageButton/PageButton';
import FactionsCarousel from '../../EmblaCarousel/FactionsCarousel';
import FactionUnitSkinsCarousel from '../../FactionUnitSkinsCarousel/FactionUnitSkinsCarousel';
import {usePageAnalytics} from '../../../hooks/analytics/usePageAnalytics';
import {FACTION_CONFIGS} from '../../../configs/factions';
import {FACTION_UNIT_SKIN_CONFIGS} from '../../../configs/factionUnitsSkins';
import {COLORS_CONFIG, PLAYER_COLORS} from '../../../configs/colors';
import {
  ALL_FACTION_SKINS,
  getFactionSkinImagePath,
  isFactionSkinVisible
} from '../../../configs/factions/skins/allFactionSkins';
import {getFactionImagePath} from '../../../utils/factions/getFactionImagePath';
import {getFactionRankImagePath} from '../../../utils/factions/getFactionRankImagePath';
import {getRankSkinFrameByRank} from '../../../utils/factions/getRankSkinFramePath';
import {getFactionSkinRank} from '../../../utils/factions/getFactionSkinRank';
import {getFactionSkinAccentColorKey} from '../../../utils/factions/getFactionSkinAccentColorKey';
import {API_BASE} from '../../../utils/apiConfig';
import {getCachedOrFetch} from '../../../utils/fetchCache';
import {MENU_SOUND_MANAGER} from '../../../scenes/MenuSoundManager';
import {
  addOwnedUnitSkin,
  clearFactionSkinSelection,
  updateFactionLoadout,
  updatePlayerRewards
} from '../../../redux/actions';
import {RANK_BACKGROUND_COLORS, RANK_NAME_COLORS} from "../../../constants/factions";
import {FACTION_SKIN_CURRENCIES, FACTION_SKIN_DEFAULT_TYPE} from "../../../constants/factions/skins";
import UserHeader from "../../UserHeader/UserHeader";
import {getFormattedPrice} from "../../../utils/price/getFormattedPrice";
import {
  checkIfRankSkinNewArtReady,
  getRankSkinId,
  getRankSkinLeague,
  RANK_SKIN_LEAGUES,
  RANK_SKIN_MIN_PLAYERS,
} from '../../../configs/rankSkins';
import {getLeagueConfig} from '../../../configs/leagues';
import {RANK_SKIN_DISPLAY} from '../../../utils/factions/getFactionRankSkinTitle';
import {getFactionSkinPrice} from '../../../utils/factions/getFactionSkinPrice';

const OPTIONS = {loop: true};

// Как показывать цену обличия в каждой из валют (см. getFactionSkinPrice)
const SKIN_PRICE_DISPLAY = {
  [FACTION_SKIN_CURRENCIES.NUMENS]: {icon: '/img/icons/numens.png', alt: 'нумены', className: 'text-coins'},
  [FACTION_SKIN_CURRENCIES.CRYSTALS]: {icon: '/img/icons/crystal.png', alt: 'кристаллы', className: 'text-crystals'},
};

// Баланс игрока в валюте цены
const getBalance = (auth, currency) => (
  currency === FACTION_SKIN_CURRENCIES.CRYSTALS ? (auth.crystals || 0) : (auth.numens || 0)
);

function resolveFactionColor(colorKey, fallbackColor) {
  if (colorKey === PLAYER_COLORS.BROWN) return '#7b3c00';
  if (colorKey === PLAYER_COLORS.BLUE) return '#157be0';
  return COLORS_CONFIG[colorKey] || fallbackColor;
}

function getFactionSkinSelectionColor(faction, selectedSkinId, useFactionColorAsDefault) {
  if (!selectedSkinId || selectedSkinId === `${faction.id}_default`) {
    return useFactionColorAsDefault ? faction.textColor : undefined;
  }
  const rank = getRankSkinLeague(selectedSkinId);
  if (rank && selectedSkinId === getRankSkinId(faction.id, rank)) {
    return RANK_NAME_COLORS[rank];
  }
  const skinConfig = ALL_FACTION_SKINS.find((skin) => skin.id === selectedSkinId);
  const colorKey = getFactionSkinAccentColorKey(skinConfig, faction);
  return resolveFactionColor(colorKey, faction.textColor);
}

/**
 * Страница фракции: обличия и образы отрядов.
 *
 * В своей коллекции обличие можно выбрать или купить. При просмотре коллекции
 * другого игрока (isReadOnly) страница показывает ЕГО открытые обличия и его
 * выбранный образ, но только для просмотра: ни выбрать, ни купить ничего
 * нельзя, цены и свой баланс не показываются
 */
function FactionPage({
                       factionId,
                       selectedSkinId,
                       availableFactions,
                       availableSkinIds,
                       focusUnitSkins,
                       onBack,
                       onSkinSelected,
                       onBackToInventory,
                       onGoToCrystals,
                       // Просмотр чужой коллекции
                       isReadOnly,
                       userName,
                       factionLoadouts: readOnlyFactionLoadouts,
                       ownedUnitSkinIds: readOnlyOwnedUnitSkinIds,
                     }) {
  usePageAnalytics('FactionPage');

  const dispatch = useDispatch();
  const auth = useSelector((state) => state.private.auth);
  const [ritualPhase, setRitualPhase] = useState(null);
  const [isBuying, setIsBuying] = useState(false);
  const [purchaseError, setPurchaseError] = useState(null);
  const [rankInfoOverlay, setRankInfoOverlay] = useState(null);

  const [ownedUnitSkinIds, setOwnedUnitSkinIds] = useState([]);
  const [unitSkinRitualPhase, setUnitSkinRitualPhase] = useState(null);
  const [isBuyingUnitSkin, setIsBuyingUnitSkin] = useState(false);
  const [unitSkinPurchaseError, setUnitSkinPurchaseError] = useState(null);
  const unitSkinsSectionRef = useRef(null);

  useEffect(() => {
    // В чужой коллекции образы отрядов приходят вместе с ней — свой инвентарь
    // тут ни при чем
    if (isReadOnly) {
      setOwnedUnitSkinIds(readOnlyOwnedUnitSkinIds || []);
      return undefined;
    }

    let cancelled = false;

    getCachedOrFetch('inventory', () => fetch(`${API_BASE}/inventory`, {
      method: 'GET',
      credentials: 'include',
    }).then(async (res) => {
      if (!res.ok) throw new Error('Failed to fetch inventory');
      return res.json();
    }))
      .then((data) => {
        if (cancelled) return;
        setOwnedUnitSkinIds(data.inventory?.unitSkins || []);
      })
      .catch((error) => {
        console.error('Fetch unit skins error:', error);
      });

    return () => {
      cancelled = true;
    };
  }, [isReadOnly, readOnlyOwnedUnitSkinIds]);

  useEffect(() => {
    if (!focusUnitSkins) return;
    const timeoutId = setTimeout(() => {
      unitSkinsSectionRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'});
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [focusUnitSkins]);

  const handleSelectedIndexChange = useCallback(() => {
    setIsBuying(false);
    setPurchaseError(null);
    setRankInfoOverlay(null);
  }, []);

  const handleUnitSkinSelectedIndexChange = useCallback(() => {
    setIsBuyingUnitSkin(false);
    setUnitSkinPurchaseError(null);
  }, []);

  const faction = useMemo(
    () => FACTION_CONFIGS.find((f) => f.id === factionId),
    [factionId]
  );

  const isFactionAvailable = useMemo(
    () => availableFactions?.some((f) => f.id === factionId && f.isAvailable),
    [availableFactions, factionId]
  );

  const ownedSkinIds = useMemo(
    () => new Set(availableSkinIds || []),
    [availableSkinIds]
  );

  const factionLoadouts = isReadOnly ? (readOnlyFactionLoadouts || {}) : auth.factionLoadouts;
  const selectedSkinFromLoadout = factionLoadouts?.[factionId]?.selectedSkin || `${factionId}_default`;
  const effectiveSelectedSkinId = selectedSkinId || selectedSkinFromLoadout;

  const unitSkinConfig = useMemo(
    () => FACTION_UNIT_SKIN_CONFIGS.find((config) => config.factionId === factionId),
    [factionId]
  );

  const isUnitSkinOwned = Boolean(unitSkinConfig && ownedUnitSkinIds.includes(unitSkinConfig.id));

  const unitSkinIconColor = useMemo(() => {
    if (!faction) return '#ffffff';
    return getFactionSkinSelectionColor(faction, effectiveSelectedSkinId, true);
  }, [faction, effectiveSelectedSkinId]);

  const hasUnitSkins = Boolean(faction && unitSkinConfig);

  const skins = useMemo(() => {
    if (!faction) return [];

    const nonRankSkins = ALL_FACTION_SKINS.filter(
      (skin) => skin.factionId === factionId && isFactionSkinVisible(skin, ownedSkinIds)
    );
    const availableNonRank = nonRankSkins.filter((skin) =>
      ownedSkinIds.has(skin.id)
    );
    const unavailableNonRank = nonRankSkins.filter(
      (skin) => !ownedSkinIds.has(skin.id)
    );

    const buildSkin = (skin) => ({
      ...skin,
      isSelected: skin.id === selectedSkinFromLoadout,
    });

    return [
      buildSkin({
        id: `${factionId}_default`,
        name: 'Обычный образ',
        type: FACTION_SKIN_DEFAULT_TYPE,
        factionId,
        isAvailable: isFactionAvailable,
        priceNumens: 0,
        imagePath: getFactionImagePath(factionId),
      }),
      ...availableNonRank.map((skin) => buildSkin({
        ...skin,
        type: 'non-rank',
        isAvailable: true,
        imagePath: getFactionSkinImagePath(skin.id),
      })),
      ...unavailableNonRank.map((skin) => buildSkin({
        ...skin,
        type: 'non-rank',
        isAvailable: false,
        imagePath: getFactionSkinImagePath(skin.id),
      })),
      // Рейтинговые образы — от младшей лиги к старшей. Образ, чей новый арт
      // еще не нарисован (алмазные и рубиновые нереализованных фракций),
      // стоит заглушкой «СКОРО»
      ...RANK_SKIN_LEAGUES.map((rank) => buildSkin({
        id: getRankSkinId(factionId, rank),
        name: getLeagueConfig(rank).name,
        type: 'rank',
        rank,
        factionId,
        isAvailable: ownedSkinIds.has(getRankSkinId(factionId, rank)),
        priceNumens: 0,
        imagePath: getFactionRankImagePath(factionId, rank),
        isComingSoon: !checkIfRankSkinNewArtReady(rank, factionId),
      })),
    ];
  }, [faction, factionId, isFactionAvailable, ownedSkinIds, selectedSkinFromLoadout]);

  const orderedSkins = useMemo(() => {
    if (!effectiveSelectedSkinId || skins.length === 0) return skins;
    const selectedIndex = skins.findIndex((skin) => skin.id === effectiveSelectedSkinId);
    if (selectedIndex <= 0) return skins;
    return [
      ...skins.slice(selectedIndex),
      ...skins.slice(0, selectedIndex),
    ];
  }, [skins, effectiveSelectedSkinId]);

  const resolveSkinColor = (skin) => {
    if (skin.type === FACTION_SKIN_DEFAULT_TYPE) return 'var(--text-secondary-color)';
    if (skin.rank && RANK_NAME_COLORS[skin.rank]) {
      return RANK_NAME_COLORS[skin.rank];
    }
    const colorKey = getFactionSkinAccentColorKey(skin, faction);
    return resolveFactionColor(colorKey, faction.textColor);
  };

  const slides = useMemo(
    () => Array.from(Array(orderedSkins.length).keys()),
    [orderedSkins.length]
  );

  const performSelectAnimation = (skin) => {
    MENU_SOUND_MANAGER.playFactionSelect(skin.factionId);
    setRitualPhase('phase1');
    setTimeout(() => setRitualPhase('phase2'), 300);
    setTimeout(() => {
      setRitualPhase('phase3');
      onSkinSelected?.();
    }, 1800);
  };

  const handleSelectSkin = (skin) => {
    if (ritualPhase || skin.isSelected) return;

    performSelectAnimation(skin);

    fetch(`${API_BASE}/select-skin`, {
      method: 'POST',
      credentials: 'include',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({factionId: skin.factionId, skinId: skin.id}),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to select skin');
        }
        if (skin.id === `${skin.factionId}_default`) {
          dispatch(clearFactionSkinSelection(skin.factionId));
        } else {
          dispatch(updateFactionLoadout(skin.factionId, {selectedSkin: skin.id}));
        }
      })
      .catch((error) => {
        console.error('Select skin error:', error);
      });
  };

  const handleBuySkin = (skin) => {
    if (ritualPhase || skin.isSelected || isBuying || purchaseError) return;

    const price = getFactionSkinPrice(skin);
    if (!price) return;

    const balance = getBalance(auth, price.currency);
    if (balance < price.amount) {
      setPurchaseError({skinId: skin.id, missingAmount: price.amount - balance, currency: price.currency});
      setTimeout(() => setPurchaseError(null), 3000);
      return;
    }

    setIsBuying(true);

    fetch(`${API_BASE}/buy-skin`, {
      method: 'POST',
      credentials: 'include',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({factionId: skin.factionId, skinId: skin.id}),
    })
      .then(async (response) => {
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.error || 'Failed to buy skin');
        }
        return response.json();
      })
      .then(() => {
        if (price.currency === FACTION_SKIN_CURRENCIES.CRYSTALS) {
          dispatch(updatePlayerRewards(0, undefined, false, -price.amount));
        } else {
          dispatch(updatePlayerRewards(-price.amount, undefined, false));
        }
        dispatch(updateFactionLoadout(skin.factionId, {selectedSkin: skin.id}));
        setIsBuying(false);
        performSelectAnimation(skin);
      })
      .catch((error) => {
        console.error('Buy skin error:', error);
        setIsBuying(false);
      });
  };

  const performUnitSkinPurchaseAnimation = (unitSkinItem) => {
    MENU_SOUND_MANAGER.playFactionSelect(unitSkinItem.factionId);
    setUnitSkinRitualPhase('phase1');
    setTimeout(() => setUnitSkinRitualPhase('phase2'), 300);
    setTimeout(() => setUnitSkinRitualPhase('phase3'), 1800);
    setTimeout(() => setUnitSkinRitualPhase(null), 3000);
  };

  const handleBuyUnitSkin = (unitSkinItem) => {
    if (unitSkinRitualPhase || unitSkinItem.isOwned || isBuyingUnitSkin || unitSkinPurchaseError || !unitSkinConfig) return;

    if (auth.numens < unitSkinItem.priceNumens) {
      setUnitSkinPurchaseError({
        unitTypeId: unitSkinItem.unitTypeId,
        missingAmount: unitSkinItem.priceNumens - auth.numens
      });
      setTimeout(() => setUnitSkinPurchaseError(null), 3000);
      return;
    }

    setIsBuyingUnitSkin(true);

    fetch(`${API_BASE}/buy-unit-skin`, {
      method: 'POST',
      credentials: 'include',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({factionId: unitSkinItem.factionId}),
    })
      .then(async (response) => {
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.error || 'Failed to buy unit skin');
        }
        return response.json();
      })
      .then((data) => {
        dispatch(updatePlayerRewards(-unitSkinItem.priceNumens, undefined, false));
        dispatch(addOwnedUnitSkin(data.unitSkinId));
        setOwnedUnitSkinIds((prev) => (prev.includes(data.unitSkinId) ? prev : [...prev, data.unitSkinId]));
        setIsBuyingUnitSkin(false);
        performUnitSkinPurchaseAnimation(unitSkinItem);
      })
      .catch((error) => {
        console.error('Buy unit skin error:', error);
        setIsBuyingUnitSkin(false);
      });
  };

  const handleUnitSkinRitualStart = (unitSkinItem) => {
    if (isReadOnly || unitSkinItem.isOwned) return;
    handleBuyUnitSkin(unitSkinItem);
  };

  const handleRankInfoClick = (skin) => {
    if (ritualPhase || skin.isSelected || rankInfoOverlay) return;
    setRankInfoOverlay({skinId: skin.id, rank: skin.rank});
    setTimeout(() => setRankInfoOverlay(null), 8000);
  };

  const handleRitualStart = (skin) => {
    if (isReadOnly || skin.isSelected) return;

    if (skin.isAvailable) {
      handleSelectSkin(skin);
      return;
    }

    if (skin.type === 'rank') {
      handleRankInfoClick(skin);
      return;
    }

    if (getFactionSkinPrice(skin)) {
      handleBuySkin(skin);
    }
  };

  if (!faction) return null;

  const isAnyRitualActive = Boolean(ritualPhase || unitSkinRitualPhase);

  return (
    <>
      <PageButton
        className="faction-page-floating-back-button"
        onClick={onBackToInventory}
        pageId="FactionPage"
        buttonId="back_to_inventory_floating"
      >
        <img
          className="faction-page-floating-back-button_icon"
          src="/img/icons/back_navigation.png"
          alt="К коллекции"
          title="К коллекции"
        />
      </PageButton>

      {/* Свой баланс — только в своей коллекции: в чужой покупать нечего */}
      {!isReadOnly && (
        <UserHeader
          pageId={'FactionPage'}
          className={`faction-page-header ${isAnyRitualActive ? 'ritual-siblings--fading' : ''}`}
          onGoToCrystals={onGoToCrystals}
        />
      )}

      <h1
        className={`lobby-page-title faction-page-title ${isAnyRitualActive ? 'ritual-siblings--fading' : ''}`}
        style={{color: faction.textColor}}
      >
        {faction.name}
        {isReadOnly && userName && (
          <span className={'faction-page-title_user'}>{userName}</span>
        )}
      </h1>

      <div className={`faction-page-carousel-wrapper ${unitSkinRitualPhase ? 'ritual-siblings--fading' : ''}`}>
      <FactionsCarousel
        slides={slides}
        items={orderedSkins}
        options={OPTIONS}
        onRitualStart={handleRitualStart}
        onSelectedIndexChange={handleSelectedIndexChange}
        ritualPhase={ritualPhase}
        getItemImagePath={(skin) => skin.imagePath}
        // Новый арт лиги еще не нарисован: вместо образа — черная заглушка
        getItemImageElement={(skin) => skin.isComingSoon && (
          <div className="faction-page-coming-soon">
            <span className="faction-page-coming-soon-label">Скоро</span>
          </div>
        )}
        // Рейтинговый образ в карусели идет в рамке своей лиги
        getItemFramePath={(skin) => getRankSkinFrameByRank(skin.rank)}
        // Обычное обличие — с шевронами своего рейтинга в углу картинки
        getItemSkinRank={(skin) => getFactionSkinRank(skin.id, factionId)}
        getItemName={(skin) => skin.name}
        getItemImageContainerStyle={(skin) => {
          if (skin.isSelected && skin.type !== FACTION_SKIN_DEFAULT_TYPE) {
            return {backgroundColor: resolveSkinColor(skin)};
          }
          if (skin.rank && RANK_BACKGROUND_COLORS[skin.rank]) {
            return {backgroundColor: RANK_BACKGROUND_COLORS[skin.rank]};
          }
          return undefined;
        }}
        getItemOverlay={(skin) => {
          if (purchaseError?.skinId === skin.id) {
            const priceDisplay = SKIN_PRICE_DISPLAY[purchaseError.currency];
            return (
              <div className="faction-page-purchase-error-overlay">
                <div className="faction-page-purchase-error-text">
                  Не хватает{' '}
                </div>
                <div className="faction-page-purchase-error-text">
                  <span className={priceDisplay.className}>
                    {getFormattedPrice(purchaseError.missingAmount)}
                  </span>{' '}
                  <img
                    className="faction-page-purchase-error-icon"
                    src={priceDisplay.icon}
                    alt={priceDisplay.alt}
                  />
                </div>
              </div>
            );
          }

          if (rankInfoOverlay?.skinId === skin.id) {
            const rankColor = RANK_NAME_COLORS[rankInfoOverlay.rank];
            const leagueName = RANK_SKIN_DISPLAY[rankInfoOverlay.rank]?.leagueAdjectiveGenitive;
            const leagueIcon = getLeagueConfig(rankInfoOverlay.rank)?.iconPath;
            return (
              <div className="faction-page-rank-info-overlay faction-page-rank-info-overlay-rank">
                <img
                  className="faction-page-rank-info-icon"
                  src={leagueIcon}
                  alt={leagueName}
                />
                <div className="faction-page-rank-info-text">
                  Чтобы получить скин
                </div>
                <div className="faction-page-rank-info-text">
                  займите{' '}
                  <span style={{color: rankColor}}>
                    1 место
                  </span>
                </div>
                <div className="faction-page-rank-info-text">
                  за фракцию{' '}
                  <span style={{color: faction.textColor}}>
                    {faction.name}
                  </span>
                </div>
                <div className="faction-page-rank-info-text">
                  в рейтинговой битве
                </div>
                <div className="faction-page-rank-info-text">
                  от {RANK_SKIN_MIN_PLAYERS} игроков
                </div>
                <div className="faction-page-rank-info-text">
                  со средним рангом
                </div>
                <div className="faction-page-rank-info-text">
                  <span style={{color: rankColor}}>
                    {leagueName} лиги
                  </span>
                </div>
              </div>
            );
          }

          return null;
        }}
        getItemDescription={(skin) => {
          if (skin.type === 'rank') {
            const rarity = RANK_SKIN_DISPLAY[skin.rank]?.rarityNeuter || '';
            return `${rarity.charAt(0).toUpperCase()}${rarity.slice(1)} обличие`;
          }
          const price = getFactionSkinPrice(skin);
          if (!isReadOnly && !skin.isAvailable && skin.type === 'non-rank' && price) {
            const priceDisplay = SKIN_PRICE_DISPLAY[price.currency];
            return (
              <span className={`faction-page-skin-price ${priceDisplay.className}`}>
                {getFormattedPrice(price.amount)}
                <img
                  className="faction-page-skin-price-icon"
                  src={priceDisplay.icon}
                  alt={priceDisplay.alt}
                />
              </span>
            );
          }
          return null;
        }}
        getItemTextColor={resolveSkinColor}
        getItemNameClassName={(/*skin*/) => {
          // if (skin.rank === 'ruby') return 'text-rating';
          // if (skin.rank === 'gold') return 'text-coins-dark';
          return '';
        }}
        isItemAvailable={(skin) => skin.isAvailable || skin.type === FACTION_SKIN_DEFAULT_TYPE}
        isSelectDisabled={(skin) => {
          if (isReadOnly || skin.isSelected) return true;
        }}
        selectButtonLabel={(skin) => {
          if (skin.isSelected) {
            return 'Выбран';
          }
          // В чужой коллекции кнопка — просто состояние обличия
          if (isReadOnly) {
            return (skin.isAvailable || skin.type === FACTION_SKIN_DEFAULT_TYPE) ? 'Открыт' : 'Не открыт';
          }
          // Порядок веток — как в handleRitualStart: открытый образ выбирается
          // независимо от типа, и рейтинговый в том числе
          if (skin.isAvailable) {
            return 'Выбрать';
          }
          if (skin.type === 'rank') {
            return 'Получить';
          }
          if (getFactionSkinPrice(skin)) {
            if (isBuying) {
              return 'Покупка...';
            }
            return 'Купить';
          }
          return 'Выбрать';
        }}
        disableMaskLabel={(skin) => (skin.isComingSoon ? '' : 'Не открыто')}
        showDetailsButton={false}
        pageId="FactionPage"
        selectButtonId="skin_action"
      />
      </div>

      {hasUnitSkins && (
        <div
          ref={unitSkinsSectionRef}
          className={`faction-page-carousel-wrapper ${ritualPhase ? 'ritual-siblings--fading' : ''}`}
        >
          <h2
            className={`faction-page-unit-skins-title ${isAnyRitualActive ? 'ritual-siblings--fading' : ''}`}
            style={{color: faction.textColor}}
          >
            Образы отрядов
          </h2>

          <FactionUnitSkinsCarousel
            factionId={factionId}
            iconColor={unitSkinIconColor}
            isOwned={isUnitSkinOwned}
            ritualPhase={unitSkinRitualPhase}
            onRitualStart={handleUnitSkinRitualStart}
            onSelectedIndexChange={handleUnitSkinSelectedIndexChange}
            isSelectDisabled={isReadOnly ? () => true : undefined}
            getItemOverlay={(item) => {
              if (unitSkinPurchaseError?.unitTypeId !== item.unitTypeId) return null;
              return (
                <div className="faction-page-purchase-error-overlay">
                  <div className="faction-page-purchase-error-text">
                    Не хватает{' '}
                  </div>
                  <div className="faction-page-purchase-error-text">
                    <span className="text-coins">
                      {getFormattedPrice(unitSkinPurchaseError.missingAmount)}
                    </span>{' '}
                    <img
                      className="faction-page-purchase-error-icon"
                      src="/img/icons/numens.png"
                      alt="нумены"
                    />
                  </div>
                </div>
              );
            }}
            selectButtonLabel={(item) => {
              if (item.isOwned) {
                return 'Открыто';
              }
              if (isReadOnly) {
                return 'Не открыто';
              }
              if (isBuyingUnitSkin) {
                return 'Покупка...';
              }
              return (
                <span className="faction-page-skin-price text-coins">
                  {getFormattedPrice(item.priceNumens)}
                  <img
                    className="faction-page-skin-price-icon"
                    src="/img/icons/numens.png"
                    alt="нумены"
                  />
                </span>
              );
            }}
            pageId="FactionPage"
          />
        </div>
      )}

      {!isReadOnly && !isUnitSkinOwned && (
        <div className={'faction-page_unit-skins-price-comment'}>Цена за весь набор образов</div>
      )}

      <div className={`main-menu-buttons ${isAnyRitualActive ? 'ritual-siblings--fading' : ''}`}>
        <PageButton
          className="main-menu-button"
          onClick={onBack}
          pageId="FactionPage"
          buttonId="back"
        >
          Назад
        </PageButton>
      </div>
    </>
  );
}

export default FactionPage;
